import React, { useState } from 'react';
import { logger } from '../../utils/logger';
import { useFamilyStore } from '../../stores/familyStore';
import { useAuthStore } from '../../stores/authStore';
import { MemberRoleManager } from './MemberRoleManager';
import type { FamilyRole } from '../../utils/accessControl';
import { PrimaryParentOnly } from '../security/PermissionGate';
import { InvitationsList } from './InvitationsList';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

/**
 * Component displaying family members list with access control
 */
export const FamilyMembersList: React.FC = () => {
  const { user } = useAuthStore();
  const { family, members, fetchFamilyMembers, isLoading, error } = useFamilyStore();
  const [refreshInvitationsFlag, setRefreshInvitationsFlag] = useState(0);
  const bumpInvitationsRefresh = () => setRefreshInvitationsFlag(v => v + 1)

  const getUserInitials = (email?: string) => {
    const userEmail = email || user?.email
    if (!userEmail) return '?'

    const nameParts = userEmail.split('@')[0].split('.')

    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase()
    }

    return userEmail.substring(0, 2).toUpperCase()
  }

  if (!family) {
    return <div className="text-center py-4">No active family</div>;
  }

  if (isLoading) {
    return <div className="text-center py-4">Loading family members...</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-error">Error: {error}</div>;
  }

  const normalizeRoleValue = (role: string): FamilyRole => {
    const cleanRole = String(role).trim().toLowerCase();

    if (cleanRole.includes('parent_a')) return 'parent_a';
    if (cleanRole.includes('parent_b')) return 'parent_b';
    if (cleanRole.includes('child_viewer')) return 'child_viewer';

    return 'child_viewer';
  };

  const getRoleName = (role: FamilyRole): string => {
    const normalizedRole = normalizeRoleValue(role);

    switch (normalizedRole) {
      case 'parent_a':
        return 'Parent / Guardian (Admin)';
      case 'parent_b':
        return 'Parent / Guardian';
      case 'child_viewer':
        return 'Viewer';
      default:
        return String(role);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Family Members</h2>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          No family members
        </div>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="border-b pb-3 last:border-0">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium mr-3">
                  {getUserInitials(member.user_email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-600">
                    {member.user_email || member.user_id}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full bg-green-500 mr-2`}></span>
                    <span>{getRoleName(member.role as FamilyRole)}</span>
                  </div>

                  <div className="mt-2 flex justify-end">
                    <div className="w-full sm:w-72">
                      <MemberRoleManager
                        userId={member.user_id}
                        currentRole={member.role as FamilyRole}
                        onRoleUpdated={() => fetchFamilyMembers(family.id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PrimaryParentOnly fallback={
        <div className="mt-6 border-t pt-6">
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-xl mb-2">🔒</div>
            <div className="font-medium">Only administrator can invite new members</div>
            <div className="text-sm mt-1">
              Contact the family administrator to add new members.
            </div>
          </div>
        </div>
      }>
        <div className="mt-6 border-t pt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs mr-2 ${members.length >= 8 ? 'bg-gray-400' : 'bg-blue-500'
              }`}>+</span>
            Invite new family member
          </h3>
          <InviteMemberForm onInvitationsUpdated={bumpInvitationsRefresh} />
        </div>

        {/* Simple role explanation */}
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Roles and permissions</h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="font-medium">Parent / Guardian (Admin)</span> — full editing and admin functions</li>
            <li><span className="font-medium">Parent / Guardian</span> — full editing</li>
            <li><span className="font-medium">Viewer</span> — access to Care, Notes, and Expenses — read only</li>
          </ul>
        </div>

        {/* Sent invitations list */}
        <div className="mt-6">
          <InvitationsList key={refreshInvitationsFlag} />
        </div>
      </PrimaryParentOnly>
    </div>
  );
};

/**
 * Invite new member form
 */
const InviteMemberForm: React.FC<{ onInvitationsUpdated?: () => void }> = ({ onInvitationsUpdated }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<FamilyRole>('parent_b');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const { user } = useAuthStore();
  const { family, members } = useFamilyStore();
  const entitlements = useEntitlements(family?.id);
  const createInvitation = useMutation(api.invitations.mutations.createInvitation);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!family || !email || !user) return;

    // Block invitation for Basic plan
    if (!entitlements || entitlements.planId === 'basic') {
      alert('Co-parenting and collaborative editing features are available in the paid Pro plan');
      return;
    }

    // Prevent self-invitation
    if (user.email && email.trim().toLowerCase() === user.email.trim().toLowerCase()) {
      setSubmitError('You cannot invite yourself');
      setTimeout(() => setSubmitError(null), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      // Check max members limit (8)
      if (members.length >= 8) {
        throw new Error('Maximum 8 members allowed in family');
      }

      // Check parents limit if inviting parent_b
      if (role === 'parent_b') {
        const parentCount = members.filter((member) =>
          member.role === 'parent_a' || member.role === 'parent_b'
        ).length;

        if (parentCount >= 2) {
          throw new Error('Maximum 2 parents allowed in family (parent_a + parent_b)');
        }

        logger.debug(`Limits check: ${parentCount}/2 parents in family`);
      }

      // Check observers limit
      if (role !== 'parent_b') {
        const observerCount = members.filter((m) => m.role !== 'parent_a' && m.role !== 'parent_b').length;
        if (observerCount >= entitlements.maxObservers) {
          throw new Error(`Observer limit reached in plan (max. ${entitlements.maxObservers}).`);
        }
      }

      logger.debug(`Members limit check: ${members.length}/8`);

      const result = await createInvitation({
        email: email.trim(),
        role: role as 'parent_a' | 'parent_b' | 'child_viewer',
        familyId: family.id as Id<"families">,
      });

      logger.debug('Invitation created:', result);

      setSubmitSuccess(`Invitation sent to ${email}`);
      setEmail('');

      setTimeout(() => setSubmitSuccess(null), 3000);

      onInvitationsUpdated?.();

    } catch (error: any) {
      console.error('Invitation error:', error);
      setSubmitError(error.message || 'Failed to create invitation');

      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="bg-blue-50 p-3 rounded-lg border border-blue-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="email" className="text-sm font-medium text-blue-700 block mb-1">
              Email address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@email.com"
              className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          <div>
            <label htmlFor="role" className="text-sm font-medium text-blue-700 block mb-1">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as FamilyRole)}
              className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <PrimaryParentOnly>
                <option value="parent_b">Parent / Guardian</option>
              </PrimaryParentOnly>
              <option value="child_viewer">Viewer</option>
            </select>
          </div>
        </div>

        {submitError && (
          <div className="text-sm text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className="text-sm text-green-600 mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
            {submitSuccess}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md transition-colors flex items-center"
          >
            {isSubmitting ? (
              <>
                <span className="mr-2">Sending...</span>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </>
            ) : (
              <>
                <span className="mr-2">Send invitation</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"></path>
                </svg>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
